// Phase 15 Plan 06 Task 2 — Create-match cluster (2×2 mode-tile grid).
// Folk-art reskin (Phase 3): the four mode tiles render as white/card
// surfaces with a house-accent folk glyph + title (prototype LobbyScreen
// mode grid), NOT solid-gold chunky-pill buttons.
//
// Four primary mode CTAs (Casual / Ranked / Bot / Friend) per the
// CONTEXT.md surface order. Each tap routes through `onModeSelected(kind)`
// to the parent (LobbyScreen), which opens the corresponding modal.
//
// DEC-009 cap gating (LOCKED): Casual / Ranked / Friend are disabled when
// `$isAtCap` is true. **Bot is ALWAYS enabled** — bot matches do not
// count toward the active-match cap. A disabled tap dispatches a
// `runegambit:toast` event with `id='social.error.cap_reached'` and does
// NOT invoke onModeSelected (POLISH-06 foundational baseline — focusable
// when disabled so keyboard activation also fires the cap-reached toast).
//
// Pattern S3 (15-PATTERNS.md, derived from PhaseBPills:98-146) — but a
// 2×2 grid instead of a single row. Each pill carries an inline SVG
// mode-glyph above its label.

import { useStore } from '@nanostores/react'
import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { $isAtCap } from '../../stores/activeMatchCount'

export type CreateMatchMode = 'casual' | 'ranked' | 'bot' | 'friend'

export interface CreateMatchClusterProps {
  readonly onModeSelected: (kind: CreateMatchMode) => void
}

interface ModeDef {
  readonly kind: CreateMatchMode
  readonly messageId: string
  readonly ariaLabelId: string
  readonly ariaDefault: string
  readonly defaultLabel: string
  readonly glyph: JSX.Element
}

// Inline SVG mode-glyphs — the prototype's folk glyphs (icons.jsx +
// LobbyScreen ModeGlyph): casual = crossed SWORDS (Ember), ranked = folk
// CROWN (gold), bot = folk SPROUT (leaf), friend = folk FOLKSTAR (Frost).
// On the card-surface mode tiles each glyph carries its own accent colour via
// inline var(--*) fill/stroke (the prototype renders each glyph in its house
// accent on a white/card tile, NOT a solid-gold button foreground).
// SVG <title> elements are intentionally omitted — the parent <svg> is
// aria-hidden="true" so any inner <title> is suppressed by assistive tech
// anyway. Visible labels live in the sibling <FormattedMessage> below the
// glyph (lobby.create.{casual,ranked,bot,friend}). Inline <title> would
// also trip the Phase 18 POLISH-12 i18n-coverage gate which scans for
// raw JSX text children. (Plan 18-08 Task 1 cleanup.)
const CasualGlyph = (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    stroke="var(--color-player-a)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* folk crossed swords (prototype ICON.swords, Ember stroke) */}
    <path d="M14.5 4H20v5.5L9 20.5 3.5 15z" />
    <path d="M9.5 4H4v5.5L15 20.5 20.5 15" />
    <path d="M11 13l2 2" />
  </svg>
)

const RankedGlyph = (
  <svg
    aria-hidden="true"
    viewBox="0 0 60 44"
    width="26"
    height="19"
    fill="var(--color-accent-gold)"
    stroke="var(--color-accent-gold-deep)"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* folk crown (prototype icons.jsx Crown path, gold enamel) */}
    <path d="M6 38 L3 12 L18 25 L30 6 L42 25 L57 12 L54 38 Z" />
    <path d="M8 38 H52" />
  </svg>
)

const BotGlyph = (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    stroke="var(--color-accent-leaf)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* folk sprout (prototype ICON.sprout, leaf stroke) */}
    <path d="M12 21V10" />
    <path d="M12 13c-3 0-5-1.6-5-5 3 0 5 1.6 5 5zM12 11c3-.7 4.6-3 4.6-6C13.6 5 12 7.3 12 11z" />
  </svg>
)

const FriendGlyph = (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="var(--color-player-b)"
    stroke="none"
  >
    {/* folk star (prototype icons.jsx FolkStar path, Frost fill) */}
    <path d="M12 4c.6 5 2 6.4 7 7-5 .6-6.4 2-7 7-.6-5-2-6.4-7-7 5-.6 6.4-2 7-7z" />
  </svg>
)

const MODES: ReadonlyArray<ModeDef> = [
  {
    kind: 'casual',
    messageId: 'social.lobby.create.casual',
    ariaLabelId: 'social.lobby.create.casual_aria',
    ariaDefault: 'Start a Casual match',
    defaultLabel: 'Casual',
    glyph: CasualGlyph,
  },
  {
    kind: 'ranked',
    messageId: 'social.lobby.create.ranked',
    ariaLabelId: 'social.lobby.create.ranked_aria',
    ariaDefault: 'Start a Ranked match',
    defaultLabel: 'Ranked',
    glyph: RankedGlyph,
  },
  {
    kind: 'bot',
    messageId: 'social.lobby.create.bot',
    ariaLabelId: 'social.lobby.create.bot_aria',
    ariaDefault: 'Practice vs a Bot',
    defaultLabel: 'Bot',
    glyph: BotGlyph,
  },
  {
    kind: 'friend',
    messageId: 'social.lobby.create.friend',
    ariaLabelId: 'social.lobby.create.friend_aria',
    ariaDefault: 'Challenge a Friend',
    defaultLabel: 'Friend',
    glyph: FriendGlyph,
  },
]

function dispatchCapReachedToast(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent('runegambit:toast', {
      detail: { id: 'social.error.cap_reached' },
    }),
  )
}

export function CreateMatchCluster({
  onModeSelected,
}: CreateMatchClusterProps): JSX.Element {
  const isAtCap = useStore($isAtCap)
  const intl = useIntl()

  return (
    <div
      data-section="create-match-cluster"
      data-testid="create-match-cluster"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        width: '100%',
      }}
    >
      {MODES.map((mode) => {
        // DEC-009: bot is excluded from the cap. Casual/Ranked/Friend
        // disable when at-cap.
        const disabled = mode.kind !== 'bot' && isAtCap
        const handleClick = (): void => {
          if (disabled) {
            dispatchCapReachedToast()
            return
          }
          onModeSelected(mode.kind)
        }
        const ariaLabel = intl.formatMessage({
          id: mode.ariaLabelId,
          defaultMessage: mode.ariaDefault,
        })
        return (
          <button
            key={mode.kind}
            type="button"
            data-mode={mode.kind}
            data-testid={`create-match-${mode.kind}`}
            aria-label={ariaLabel}
            aria-disabled={disabled ? 'true' : 'false'}
            // Keep focusable when at-cap (POLISH-06 baseline) — set
            // `disabled` only when truly inactionable. We use
            // aria-disabled + a runtime guard in handleClick so the
            // cap-reached toast fires on keyboard activation too.
            onClick={handleClick}
            className="mode-tile"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              gap: 'var(--space-2)',
              minHeight: '88px',
              padding: 'var(--space-3)',
              textAlign: 'left',
              // Prototype: white/card mode tiles with a warm tile drop — NOT
              // solid-gold buttons. The folk glyph carries the house accent;
              // the title sits in dark ink on the near-white card surface.
              background: 'var(--color-card)',
              color: 'var(--color-text-primary)',
              border: 'none',
              borderRadius: 'var(--radius-tile)',
              boxShadow: 'var(--shadow-tile)',
              fontFamily: 'Fredoka, sans-serif',
              fontWeight: 700,
              fontSize: 'var(--font-size-16)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.45 : 1,
              transition: 'transform var(--duration-fast) var(--ease-out)',
            }}
          >
            <span aria-hidden="true" style={{ display: 'inline-flex' }}>
              {mode.glyph}
            </span>
            <FormattedMessage
              id={mode.messageId}
              defaultMessage={mode.defaultLabel}
            />
          </button>
        )
      })}
    </div>
  )
}
